import { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useQuery, useMutation } from "@tanstack/react-query"
import { ColumnDef } from "@tanstack/react-table"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { ExternalLinkIcon, Grid, Loader, PlusCircle, Table, X } from "lucide-react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"

// ** UI Component
import { Input } from "@components/common/input"
import { Badge } from "@components/common/badge"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import { DataTable } from "@components/common/data-table/data-table"
import { DataTableColumnHeader } from "@components/common/data-table/data-table-column-header"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/common/dialog"
import { Label } from "@pma/components/common/label"
import { RadioGroup, RadioGroupItem } from "@components/common/radio-group"
import { Separator } from "@radix-ui/react-separator"
import { Popover, PopoverTrigger, PopoverContent } from "@components/common/popover"
import { MixerHorizontalIcon } from "@radix-ui/react-icons"

// ** Types
import { DictType } from "@pma/types"

// ** Store
import { useAuth } from "@stores/auth-store"
import { useModal } from "@stores/modal-store"

// ** Api Routes
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import {
  SERVER_URL,
  STATUS_DICTIONARY_ENDPOINT,
  ADD_PROJECT_CUSTOM_COLUMNS_ENDPOINT,
  DELETE_PROJECT_CUSTOM_COLUMNS_ENDPOINT,
  GET_PROJECT_CUSTOM_COLUMNS_ENDPOINT,
} from "@pma/utils/api-routes"

// ** Constants
import { ACCESS_TOKEN, COLUMN_TYPE_OPTIONS } from "@pma/utils/constants"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Context
import { ProjectContext } from "@pma/contexts/ProjectContext"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"
import { toast } from "@pma/hooks/use-toast"

// ** Custom Components
import ProjectCard from "./project-card"
import { projectsColumn } from "./project-table-columns"
import { FilterProjectsTable } from "./filter-projects-table"
import ProjectCustomEditor from "./project-custom-editor"

interface Props {
  sm?: boolean
}

const SCROLL_CONTAINER_ID = "projects-scroll-container"

const ProjectsTable = ({ sm }: Props) => {
  const modal = useModal()
  const location = useLocation()
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const [cardView, setCardView] = useState(!!sm)
  const [isDragging, setIsDragging] = useState(false)
  const [globalFilter, setGlobalFilter] = useState("")
  const [newColumnName, setNewColumnName] = useState("")
  const [projects, setProjects] = useState<DictType[]>([])
  const [debouncedFilter, setDebouncedFilter] = useState("")
  const [columnType, setColumnType] = useState<string>("Text")
  const [addColumnDialogOpen, setAddColumnDialogOpen] = useState(false)
  const [dropdownList, setDropdownList] = useState<string[]>([])
  const [dropdownInput, setDropdownInput] = useState("")

  const isHebrew = locale === "he"
  const pathname = location.pathname
  const orgId = authConfig?.last_organization_id
  const queryKey = searchParam.get("revalidate")
  const showDeleted = searchParam.get("showDeleted")
  const newParams = new URLSearchParams(searchParam)
  const statusId = parseInt(searchParam.get("status") || "0")
  const ownerId = parseInt(searchParam.get("ownerId") || "0")
  const organizationUsers = authConfig?.organizationUsers || []
  const searchKey = debouncedFilter ? `-${debouncedFilter}` : ""
  const searchKeyArray = searchKey.split("-").filter(Boolean)

  const { projectResult, projectPagination, setSearchTerm } = useContext(ProjectContext)

  const {
    data: projectData,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = projectResult || {}

  // Debounce search filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(globalFilter)
      setSearchTerm(globalFilter.trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [globalFilter])

  const getProjectStatus = async () => {
    const response = await axios.get(SERVER_URL + STATUS_DICTIONARY_ENDPOINT, {
      params: { context_id: orgId, for_project: true },
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  const { data: projectStatuses } = useQuery({
    queryKey: ["project-status"],
    queryFn: getProjectStatus,
  })

  // ** Project Custom Columns
  const getProjectCustomColumns = async () => {
    const response = await axios.get(SERVER_URL + GET_PROJECT_CUSTOM_COLUMNS_ENDPOINT, {
      params: { organization_id: orgId },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const { data: projectCustomColumns = [], refetch: refetchCustomColumns } = useQuery({
    queryKey: ["project-custom-columns", orgId],
    queryFn: getProjectCustomColumns,
    enabled: !!orgId,
  })

  const addProjectCustomColumn = async () => {
    const payload: Record<string, unknown> = {
      name: newColumnName,
      type: columnType.toLocaleLowerCase(),
      organization_id: orgId,
    }
    if (columnType.toLocaleLowerCase() === "selector" && dropdownList.length > 0) {
      payload.dropdown_list = dropdownList
    }
    const response = await axios.post(
      SERVER_URL + ADD_PROJECT_CUSTOM_COLUMNS_ENDPOINT,
      payload,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const addColumnMutation = useMutation({
    mutationKey: ["add-project-custom-column"],
    mutationFn: addProjectCustomColumn,
    onSuccess: (data) => {
      setNewColumnName("")
      setColumnType("Text")
      setDropdownList([])
      setDropdownInput("")
      setAddColumnDialogOpen(false)
      toast({
        title: translate("Success"),
        description: translate("Column added successfully"),
        variant: "success",
      })
      refetchCustomColumns()
      newParams.set("revalidate", "projects")
      navigate(`${pathname}?${newParams.toString()}`)
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to add column"),
        variant: "error",
      })
    },
  })

  const deleteProjectCustomColumn = async (columnId: string) => {
    const response = await axios.delete(
      SERVER_URL + DELETE_PROJECT_CUSTOM_COLUMNS_ENDPOINT + `/${columnId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const deleteColumnMutation = useMutation({
    mutationKey: ["delete-project-custom-column"],
    mutationFn: deleteProjectCustomColumn,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Column deleted successfully"),
        variant: "success",
      })
      refetchCustomColumns()
      newParams.set("revalidate", "projects")
      navigate(`${pathname}?${newParams.toString()}`)
    },
    onError: () => {
      toast({
        title: translate("Error"),
        description: translate("Unable to delete column"),
        variant: "error",
      })
    },
  })

  const handleAddColumn = () => {
    if (!newColumnName.trim()) return
    addColumnMutation.mutate()
  }

  // ** Dynamic columns from project custom columns (follows Table-Section.tsx pattern)
  const dynamicColumns: ColumnDef<any>[] = useMemo(() => {
    return (
      projectCustomColumns?.map((cColumn: DictType) => ({
        accessorKey: cColumn?.name,
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title={cColumn?.name} className='' />
        ),
        cell: ({ row }: DictType) => {
          const customColumns = row.original?.custom_colums
          const customValue = customColumns?.find(
            (column: DictType) => column?.name === cColumn?.name,
          )

          return (
            <ProjectCustomEditor
              id={row.original?.id}
              title={cColumn?.name}
              value={customValue?.value}
              customId={customValue?.column_id}
              columnType={customValue?.type || cColumn?.type}
              selectorList={customValue?.list || cColumn?.list}
              customFile={customValue?._custom_file}
            >
              {customValue?.value || "---"}
            </ProjectCustomEditor>
          )
        },
        sortingFn: (rowA: any, rowB: any) => {
          const customValueA = rowA.original?.custom_columns?.find(
            (column: DictType) => column?.name === cColumn?.name,
          )?.value
          const customValueB = rowB.original?.custom_columns?.find(
            (column: DictType) => column?.name === cColumn?.name,
          )?.value

          if (typeof customValueA === "number" && typeof customValueB === "number") {
            return customValueA - customValueB
          } else if (typeof customValueA === "string" && typeof customValueB === "string") {
            return customValueA.localeCompare(customValueB)
          } else {
            return 0
          }
        },
        enableSorting: true,
      })) || []
    )
  }, [projectCustomColumns])

  // ** Merge static project columns with dynamic custom columns
  const columns = useMemo(() => {
    const staticColumns = [...projectsColumn]
    // Insert dynamic columns before the last "actions" column
    const actionsIndex = staticColumns.findIndex((col: any) => col.id === "actions")
    if (actionsIndex !== -1) {
      staticColumns.splice(actionsIndex, 0, ...dynamicColumns)
    } else {
      staticColumns.push(...dynamicColumns)
    }
    return staticColumns
  }, [dynamicColumns])

  const flatProjects = useMemo(() => {
    return projectData?.pages?.flatMap((page) => page?.items ?? []) ?? []
  }, [isFetching, projectPagination?.pageSize, queryKey])

  const activeStatus = useMemo(() => {
    return (
      projectStatuses?.find((status: DictType) => {
        return String(status?.id) === String(statusId)
      }) || {}
    )
  }, [statusId])

  const activeOwner = useMemo(() => {
    return (
      organizationUsers?.find((user: DictType) => {
        return String(user?.id) === String(ownerId)
      }) || {}
    )
  }, [ownerId])

  useEffect(() => {
    if (isDragging) return
    const savedOrder = localStorage.getItem("projectOrder")
    const savedProjectIds = savedOrder ? JSON.parse(savedOrder) : []

    const allProjects = [...flatProjects]
    const uniqueProjectsMap = new Map<string, DictType>()

    allProjects.forEach((project) => {
      uniqueProjectsMap.set(project.id.toString(), project)
    })

    // Reconstruct ordered projects based on saved order
    const orderedProjects: DictType[] = []
    const usedIds = new Set<string>()

    // First, add projects in the saved order (if they still exist)
    savedProjectIds.forEach((id: string) => {
      if (uniqueProjectsMap.has(id)) {
        orderedProjects.push(uniqueProjectsMap.get(id)!)
        usedIds.add(id)
      }
    })

    // Then, append any new projects not in the saved order
    uniqueProjectsMap.forEach((project, id) => {
      if (!usedIds.has(id)) {
        orderedProjects.push(project)
      }
    })

    setProjects(orderedProjects)
  }, [flatProjects, isDragging])

  useEffect(() => {
    if (queryKey === "projects") {
      refetch?.()
      if (statusId && statusId !== 0) {
        newParams.set("status", statusId.toString())
      } else {
        newParams.delete("status")
        newParams.delete("revalidate")
      }
      navigate(`${pathname}?${newParams.toString()}`)
    }
  }, [queryKey])

  const handleRowClick = (row: DictType) => {
    navigate(`${PROJECTS_PAGE_ROUTE}/${row.original.id}`)
  }

  const handleFetchPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Handle scroll for card view infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || !cardView) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100

    if (isNearBottom && hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage?.()
    }
  }, [cardView, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragStart = () => {
    setIsDragging(true)
  }

  const handleDragEnd = (event: any) => {
    const { active, over } = event
    setIsDragging(false)

    if (active?.id !== over?.id && over) {
      setProjects((items) => {
        const activeId = active.id.replace("project_", "")
        const overId = over.id.replace("project_", "")
        const oldIndex = items.findIndex((item) => item.id.toString() === activeId)
        const newIndex = items.findIndex((item) => item.id.toString() === overId)

        if (oldIndex === -1 || newIndex === -1) {
          return items
        }

        const newItems = arrayMove(items, oldIndex, newIndex)

        // Save new order to localStorage
        const newOrder = newItems.map((item) => item.id.toString())
        localStorage.setItem("projectOrder", JSON.stringify(newOrder))

        return newItems
      })
    }
  }

  const handleRemoveFilter = (label: string) => {
    newParams.delete(label)
    navigate(`?${newParams.toString()}`)
  }

  return (
    <div>
      <Card className='my-6 space-y-3'>
        <CardHeader className='flex pb-2'>
          <div className='flex w-full flex-col gap-4'>
            <div className='flex w-full items-center justify-between'>
              <CardTitle
                style={{ textAlign: isHebrew ? "right" : "left" }}
                className='text-xl font-semibold'
              >
                {sm ? translate("Latest Projects") : translate("Projects")}
              </CardTitle>
              <div className='flex items-center gap-2'>
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      size={"icon"}
                      variant={"ghost"}
                      className='h-8 w-8 rounded-full p-1'
                      onClick={() => {
                        modal?.onOpen()
                        modal.setContext("project")
                      }}
                    >
                      <PlusCircle />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{translate("Create Project")}</TooltipContent>
                </Tooltip>
                {sm && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Link to={PROJECTS_PAGE_ROUTE}>
                        <Button
                          size={"icon"}
                          variant={"outline"}
                          className='flex h-8 w-8 items-center justify-center rounded-full text-primary'
                        >
                          <ExternalLinkIcon size={14} />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>{translate("Go to projects")}</TooltipContent>
                  </Tooltip>
                )}

                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      size={"icon"}
                      variant={"outline"}
                      className='h-8 w-8 rounded-full p-1'
                      onClick={() => setCardView(!cardView)}
                    >
                      {cardView ? <Table /> : <Grid />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {cardView ? translate("Table View") : translate("Card View")}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger>
                    <FilterProjectsTable
                      projectStatuses={projectStatuses}
                      activeStatus={activeStatus?.id || ""}
                      activeOwner={activeOwner?.id || ""}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{translate("Filter")}</TooltipContent>
                </Tooltip>

                {/* Custom Columns Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant='outline' size='sm' className='h-8'>
                      <MixerHorizontalIcon
                        className={cn("h-4 w-4", isHebrew ? "lg:ml-2" : "lg:mr-2")}
                      />
                      <span className='hidden lg:flex'>{translate("Custom Columns")}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align={isHebrew ? "start" : "end"}
                    className={cn("w-auto md:w-[350px]", isHebrew ? "rtl" : "ltr")}
                  >
                    <Label>{translate("Custom Columns")}</Label>
                    <Separator className='my-2' />
                    <div className='rounded-md bg-gray-100 p-2'>
                      <div className='flex flex-wrap gap-4 py-1'>
                        {projectCustomColumns?.length > 0 ? (
                          projectCustomColumns.map((column: DictType) => (
                            <span
                              key={column.id}
                              className='flex cursor-pointer items-center gap-1 rounded-md border bg-white p-1 text-sm capitalize'
                            >
                              {translate(column.name)}
                              <div className='flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-white'>
                                <X
                                  size={10}
                                  onClick={() => deleteColumnMutation.mutate(column?.id)}
                                />
                              </div>
                            </span>
                          ))
                        ) : (
                          <small className='text-muted-foreground'>
                            {translate("No custom columns")}
                          </small>
                        )}
                      </div>
                      <Separator className='my-2' />
                      <div
                        className={cn(
                          "flex items-center justify-end py-1",
                          isHebrew ? "rtl" : "ltr",
                        )}
                      >
                        <Dialog open={addColumnDialogOpen} onOpenChange={setAddColumnDialogOpen}>
                          <DialogTrigger asChild className={isHebrew ? "rtl" : "ltr"}>
                            <Button
                              size='sm'
                              variant='outline'
                              className='flex items-center justify-start'
                            >
                              <PlusCircle className='mx-2 h-4 w-4' />
                              {translate("Add Column")}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className={cn("z-50", isHebrew ? "rtl" : "ltr")}>
                            <DialogHeader>
                              <DialogTitle className={isHebrew ? "text-right" : "text-left"}>
                                {translate("Add New Column")}
                              </DialogTitle>
                            </DialogHeader>
                            <div className='flex w-full'>
                              <div className='w-full'>
                                <Label htmlFor='project-column-name'>
                                  {translate("Column Name")}
                                </Label>
                                <div className='flex w-full'>
                                  <Input
                                    id='project-column-name'
                                    className='w-full flex-1'
                                    value={newColumnName}
                                    onChange={(e) => setNewColumnName(e.target.value)}
                                    placeholder={translate("Enter column name")}
                                  />
                                </div>
                                <div className='w-full py-2'>
                                  <Label htmlFor='project-column-type'>
                                    {translate("Column Type")}
                                  </Label>
                                  <div className='flex w-full'>
                                    <RadioGroup defaultValue='Text' className='flex flex-wrap gap-x-4 gap-y-2'>
                                      {COLUMN_TYPE_OPTIONS.map((option, index: number) => (
                                        <div className='flex items-center space-x-2' key={index}>
                                          <RadioGroupItem
                                            value={option}
                                            id={`project-${option}`}
                                            onClick={() => setColumnType(option)}
                                          />
                                          <Label htmlFor={`project-${option}`}>
                                            {translate(option)}
                                          </Label>
                                        </div>
                                      ))}
                                    </RadioGroup>
                                  </div>
                                </div>
                                {columnType.toLocaleLowerCase() === "selector" && (
                                  <div className='w-full py-2'>
                                    <Label>{translate("Dropdown Options")}</Label>
                                    <div className='flex gap-2 py-1'>
                                      <Input
                                        value={dropdownInput}
                                        onChange={(e) => setDropdownInput(e.target.value)}
                                        placeholder={translate("Enter option")}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" && dropdownInput.trim()) {
                                            e.preventDefault()
                                            setDropdownList((prev) => [...prev, dropdownInput.trim()])
                                            setDropdownInput("")
                                          }
                                        }}
                                      />
                                      <Button
                                        type='button'
                                        size='sm'
                                        variant='outline'
                                        disabled={!dropdownInput.trim()}
                                        onClick={() => {
                                          setDropdownList((prev) => [...prev, dropdownInput.trim()])
                                          setDropdownInput("")
                                        }}
                                      >
                                        <PlusCircle className='h-4 w-4' />
                                      </Button>
                                    </div>
                                    {dropdownList.length > 0 && (
                                      <div className='flex flex-wrap gap-1 pt-1'>
                                        {dropdownList.map((item, idx) => (
                                          <span
                                            key={idx}
                                            className='flex items-center gap-1 rounded-md border bg-slate-100 px-2 py-0.5 text-sm'
                                          >
                                            {item}
                                            <X
                                              size={12}
                                              className='cursor-pointer text-destructive'
                                              onClick={() =>
                                                setDropdownList((prev) => prev.filter((_, i) => i !== idx))
                                              }
                                            />
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              onClick={handleAddColumn}
                              loading={addColumnMutation.isPending}
                              disabled={addColumnMutation.isPending || !newColumnName || (columnType.toLocaleLowerCase() === "selector" && dropdownList.length === 0)}
                            >
                              {translate("Add Column")}
                            </Button>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Search Input - only show when not sm */}
            {!sm && (
              <div className='flex w-full items-center'>
                <Input
                  placeholder={`${translate("Search")}...`}
                  value={globalFilter}
                  onChange={(event) => setGlobalFilter(event.target.value)}
                  className='h-9 w-full max-w-sm'
                />
              </div>
            )}
          </div>
        </CardHeader>

        {/* Active Filters - only show when not sm */}
        {!sm && (!!statusId || !!ownerId || showDeleted === "true" || searchKey) && (
          <div className='flex flex-wrap items-center gap-2 px-6 transition-all ease-in-out'>
            <small>{translate("Active filter")}:</small>

            {!!statusId && (
              <div className='relative w-fit'>
                <Badge className={"text-black"} style={{ background: activeStatus?.color }}>
                  {activeStatus?.name}
                </Badge>
                <X
                  size={14}
                  className={cn(
                    "absolute -top-1 cursor-pointer text-destructive",
                    isHebrew ? "-left-1" : "-right-1",
                  )}
                  onClick={() => handleRemoveFilter("status")}
                />
              </div>
            )}

            {!!ownerId && (
              <div className='relative w-fit'>
                <Badge className='text-white'>{activeOwner?.name}</Badge>
                <X
                  size={14}
                  className={cn(
                    "absolute -top-1 cursor-pointer text-destructive",
                    isHebrew ? "-left-1" : "-right-1",
                  )}
                  onClick={() => handleRemoveFilter("ownerId")}
                />
              </div>
            )}

            {showDeleted === "true" && (
              <div className='relative w-fit'>
                <Badge className='text-white'>{translate("Show Deleted Projects")}</Badge>
                <X
                  size={14}
                  className={cn(
                    "absolute -top-1 cursor-pointer text-destructive",
                    isHebrew ? "-left-1" : "-right-1",
                  )}
                  onClick={() => handleRemoveFilter("showDeleted")}
                />
              </div>
            )}

            {searchKeyArray?.map((searchItem, index) => (
              <Badge
                variant={index % 2 === 1 ? "outline" : "default"}
                key={index}
                className='font-thin'
              >
                {searchItem}
              </Badge>
            ))}

            {searchKey && (
              <X
                size={16}
                className='cursor-pointer text-red-500'
                onClick={() => {
                  setGlobalFilter("")
                  setDebouncedFilter("")
                }}
              />
            )}
          </div>
        )}

        <CardContent
          id={SCROLL_CONTAINER_ID}
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className='max-h-[700px] overflow-y-auto py-2'
        >
          {cardView ? (
            <>
              {isLoading ? (
                <div className='flex h-[20vh] w-full items-center justify-center'>
                  <Loader className='animate-spin' />
                </div>
              ) : flatProjects?.length < 1 ? (
                <div className='flex h-[20vh] w-full flex-col items-center justify-center gap-2'>
                  <span className='text-muted-foreground'>{translate("No projects found")}</span>
                  {statusId && (
                    <span className='text-sm text-muted-foreground'>
                      {translate("with status")}: {activeStatus?.name}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
                    {flatProjects?.map((project: DictType) => (
                      <ProjectCard project={project} key={project?.id} />
                    ))}
                  </div>
                  {isFetchingNextPage && (
                    <div className='flex h-[10vh] w-full items-center justify-center'>
                      <PmsLoader />
                    </div>
                  )}
                  {!hasNextPage && flatProjects?.length > 0 && (
                    <div className='flex w-full items-center justify-center py-4 text-sm text-muted-foreground'>
                      {translate("No more projects to load")}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={projects.map((project) => `project_${project.id}`)}
                strategy={verticalListSortingStrategy}
              >
                <DataTable
                  data={projects}
                  isLoading={isLoading ?? false}
                  columns={columns}
                  context='projects'
                  searchKey={searchKey}
                  enableInfiniteScroll={true}
                  fetchNextPage={handleFetchPage}
                  hasNextPage={!!hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onRowClick={handleRowClick}
                  scrollableTarget={SCROLL_CONTAINER_ID}
                />
              </SortableContext>
            </DndContext>
          )}
          {sm && cardView ? (
            <div className='mt-4 flex items-center justify-center px-2 py-2'>
              <div className='flex items-center justify-between space-x-6 lg:space-x-8'></div>
              <div className='flex items-center space-x-2'>
                {hasNextPage && (
                  <Button
                    variant='link'
                    className='h-8 w-8 p-0'
                    onClick={handleFetchPage}
                    disabled={!hasNextPage}
                    title={translate("Load more")}
                  >
                    {translate("Load more")}
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

export default ProjectsTable
